import configuration from '../configuration';
import { createStyleTag } from '../util/create-style-tag';
import tooltipCss from './css/tooltip.css';
import Cta from './index';
import { Instance, createPopper } from '@popperjs/core';
import Asset from '../asset/asset';

/**
 * Displays a tooltip to the User describing how the monthly payment is calculated, and some disclaimers.
 */
export default class OlToolTip<AssetType extends Asset> {
  component: HTMLElement;
  text: HTMLElement;
  open = false;
  popperInstance: Instance;

  /**
   * Create a new Tooltip HTML Element
   * @param asset: Asset that is being financed
   * @param cta: CTA Class to display Tooltip on.
   *             This is required to display the tooltip in the correct location.
   * @param target: HTML Element that the Tooltip should display when clicked (mobile) or hovered (desktop)
   * @param price: Price used to calculate the monthly payment.
   */
  constructor(
    public asset: AssetType,
    protected cta: Cta<AssetType>,
    public target: HTMLElement,
    public price: number
  ) {
    // Create Tooltip HTML
    this.component = document.createElement('div');

    this.component.classList.add('ol-tooltip');
    this.component.innerHTML = `<p class='ol-title ol-tooltip-text ol-inline'>Payment Shown</p>`;
    this.component.appendChild(createStyleTag(tooltipCss.toString(), 'ol-tooltip-style', {}));

    const closeButton = document.createElement('p');
    closeButton.classList.add('ol-body', 'ol-tooltip-text', 'ol-inline', 'ol-close-text');
    closeButton.innerHTML = `CLOSE X`;
    closeButton.addEventListener('click', this.closeTooltip.bind(this));
    this.component.appendChild(closeButton);

    this.text = document.createElement('p');
    this.text.classList.add('ol-body', 'ol-tooltip-text', 'ol-tooltip-text-body');
    this.component.append(this.text);

    this.popperInstance = createPopper(this.cta.shadowHost, this.component, {
      placement: 'top',
      strategy: 'absolute',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 7]
          }
        },
        {
          // custom modifier to match the width of the tooltip to the width of the widget
          name: 'sameWidth',
          enabled: true,
          phase: 'beforeWrite',
          requires: ['computeStyles'],
          fn: ({ state }) => {
            state.styles.popper.width = `${state.rects.reference.width}px`;
          },
          effect: ({ state }) => {
            state.elements.popper.style.width = `${
              //@ts-ignore offsetWidth does not exist on elements
              state.elements.offsetWidth
            }px`;
          }
        }
      ]
    });

    // Setup Tooltip Event Listeners
    this.target.addEventListener('click', () => {
      // on mobile, we use on-click
      if (window.innerWidth <= configuration.mobileBreakpoint) this.openTooltip();
    });
    this.target.addEventListener('mouseenter', () => {
      // on desktop, we use on-hover
      if (window.innerWidth > configuration.mobileBreakpoint) this.openTooltip();
    });
    this.target.addEventListener('mouseleave', () => {
      // on desktop, we use on-hover
      if (window.innerWidth > configuration.mobileBreakpoint) this.closeTooltip();
    });
  }

  /**
   * Stop displaying the Tooltip on the Webpage
   */
  closeTooltip() {
    this.component.parentNode?.removeChild(this.component);
    this.component.removeAttribute('data-show');
  }

  /**
   * Display the tooltip on the webpage
   */
  openTooltip() {
    // append tooltip directly to the CTA
    this.cta.shadowRoot.appendChild(this.component);

    const totalPrice = this.price + configuration.doc_fee;
    const totalFormattedPrice = totalPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // Re-generate the text in case a value has changed (ex, docFee)
    this.text.innerText =
      `Monthly payment calculated based upon a ${configuration.term_months} month loan, at ${configuration.apr_percent}% APR, and $${totalFormattedPrice} total ` +
      (configuration.doc_fee === 0
        ? `amount financed. Excludes tax, title, license, and doc fees. Not all buyers or vehicles will qualify.
    Minimum $100 monthly pmt required. `
        : `amt financed. Incl $${configuration.doc_fee} doc fee & excludes tax, title & license fees. Not all buyers or vehicles will qualify.
    Min $100 monthly pmt required. `) +
      `Terms subject to change without notice.`;

    this.component.setAttribute('data-show', '');
    void this.popperInstance.update();
  }
}
